import { doc, getDoc, getDocs, collection, query, where, documentId, updateDoc, setDoc, serverTimestamp, writeBatch } from "firebase/firestore";
import db from "../../firebase/firestore";
import { auth } from "../../firebase/firebase";
import { isSuggestionsV2Enabled } from "../env";

/**
 * Normalized Suggestion type for the repository layer.
 */
export interface Suggestion {
  id: string | number;
  title?: string;
  description?: string;
  category?: string;
  kind?: string;
  expected?: string;
  status?: string;
  priority?: string;
  module?: string;
  moduleName?: string;
  area?: string;
  interventi?: string[];
  [key: string]: unknown;
}

/**
 * Result type with source tracking
 */
export interface SuggestionsResult {
  suggestions: Suggestion[];
  source: "collection" | "legacy" | "empty";
}

function shouldLogSuggestionsRepo(): boolean {
  if (import.meta.env.DEV) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return localStorage.getItem('DEBUG_SUGGESTIONS_REPO') === '1';
  } catch {
    return false;
  }
}

const suggestionsLog = (message: string, detail?: Record<string, unknown>) => {
  if (!shouldLogSuggestionsRepo()) {
    return;
  }

  if (detail) {
    console.debug(`[SuggestionsRepo] ${message}`, detail);
  } else {
    console.debug(`[SuggestionsRepo] ${message}`);
  }
};

// Alias for backward compatibility
const devLog = suggestionsLog;

const getSuggestionsMode = (): "legacy" | "v2" => (isSuggestionsV2Enabled() ? "v2" : "legacy");

let modeLogged = false;
const logModeOnce = () => {
  if (modeLogged) return;
  modeLogged = true;
  const mode = getSuggestionsMode();
  const envValue = (import.meta as any)?.env?.VITE_SUGGESTIONS_V2;
  if (shouldLogSuggestionsRepo()) {
    console.debug(`[SuggestionsRepo] mode=${mode} (VITE_SUGGESTIONS_V2=${envValue})`);
  }
};

/**
 * Debug helper: get current auth context
 */
const getDebugContext = () => {
  const uid = auth.currentUser?.uid;
  const email = auth.currentUser?.email;
  return { uid, email };
};

/**
 * Safe ID encoder for composite docId: encodes special chars to underscores
 * e.g., "id-123" → "id-123", "caf/é" → "caf_é"
 */
function safeId(s: string): string {
  return encodeURIComponent(s).replace(/%/g, "_");
}

/**
 * Centralized helper: get DocumentReference for suggestions collection
 * Single source of truth to prevent writing to wrong collection (e.g., structures)
 */
function getSuggestionDocRef(structureId: string) {
  const ref = doc(db, "suggestions", structureId);
  const fullPath = ref.path;
  const isDev = import.meta.env.DEV;

  // Hard assertion: path must NOT include /structures/ (visible in both dev and prod)
  if (fullPath.includes("/structures/")) {
    const msg = `[SuggestionsRepo][FATAL] getSuggestionDocRef() produced wrong path (${fullPath}), expected suggestions/{uid}`;
    if (isDev) {
      console.error("%c" + msg, "color: red; font-weight: bold; font-size: 14px;");
    } else {
      console.error("%c" + msg, "color: red; font-weight: bold;");
    }
    // Always trace in both dev and prod
    console.trace("[SuggestionsRepo][FATAL] Stack trace for path mismatch:");
  }

  return ref;
}

/**
 * Upsert suggestion status in suggestions_status collection (new format)
 * Stores flat document with composite key: structureId__safeId(suggestionId)
 * 
 * Data format:
 * {
 *   structureId: string,
 *   suggestionId: string,
 *   status: "todo" | "doing" | "done" | "dismissed" | "",
 *   updatedAt: Timestamp (server-side),
 *   updatedBy: string (auth.uid),
 *   updatedByEmail?: string,
 * }
 * 
 * @param structureId - Structure unique identifier
 * @param suggestionId - Suggestion item ID (human-readable)
 * @param status - New status value
 * @param authUid - Current authenticated user ID
 * @param authEmail - Current authenticated user email (optional)
 */
export async function upsertSuggestionStatus(
  structureId: string,
  suggestionId: string,
  status: string,
  authUid: string,
  authEmail?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    suggestionsLog("upsertSuggestionStatus() called", {
      structureId,
      suggestionId,
      status,
      authUid,
    });

    if (!structureId || !suggestionId || !authUid) {
      suggestionsLog("❌ Missing required params", { structureId, suggestionId, authUid });
      return { success: false, error: "Missing structureId, suggestionId, or authUid" };
    }

    // Compose document ID: structureId__safeId(suggestionId)
    const docId = `${structureId}__${safeId(suggestionId)}`;
    const collectionName = "suggestions_status";
    const fullPath = `${collectionName}/${docId}`;

    // [DEBUG] Log write path with auth context
    logWritePath(collectionName, docId, ["structureId", "suggestionId", "status", "updatedAt", "updatedBy", "updatedByEmail"], "setDoc");

    const ref = doc(db, collectionName, docId);

    // [DEBUG] Validate ref path (dev/prod guard)
    if (ref.path.includes("/structures/")) {
      const msg = `[SuggestionsRepo][FATAL] upsertSuggestionStatus() docRef path contains /structures/ (${ref.path}), expected suggestions_status`;
      console.error("%c" + msg, "color: red; font-weight: bold;");
      console.trace("[SuggestionsRepo][FATAL] Stack trace:");
    }

    // Upsert with merge: true (preserve other fields if doc exists)
    await setDoc(
      ref,
      {
        structureId,
        suggestionId,
        status,
        updatedAt: serverTimestamp(),
        updatedBy: authUid,
        updatedByEmail: authEmail ?? null,
      },
      { merge: true }
    );

    devLog("Successfully upserted suggestion status", {
      structureId,
      suggestionId,
      status,
      docId,
    });

    return { success: true };
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    suggestionsLog("❌ Error upserting suggestion status", {
      structureId: structureId || "<unknown>",
      suggestionId: suggestionId || "<unknown>",
      error: errorMsg,
    });
    return { success: false, error: errorMsg };
  }
}

/**
 * Get suggestion statuses for a structure from suggestions_status collection
 * Returns a Map of suggestionId -> status for efficient merging with base suggestions
 * 
 * @param structureId - Structure unique identifier
 * @returns Map with suggestionId -> status entries, empty Map if no statuses exist
 */
export async function getSuggestionStatusesForStructure(
  structureId: string
): Promise<Map<string, string>> {
  try {
    suggestionsLog("getSuggestionStatusesForStructure() called", { structureId });

    if (!structureId) {
      return new Map();
    }

    logWritePath("suggestions_status", structureId, [], "read");

    const ref = collection(db, "suggestions_status");
    const q = query(ref, where("structureId", "==", structureId));
    const snap = await getDocs(q);

    const statusMap = new Map<string, string>();
    snap.forEach((doc) => {
      const data = doc.data() as Record<string, unknown> | undefined;
      const suggestionId = data?.suggestionId;
      const status = data?.status;

      if (suggestionId && typeof status === "string") {
        statusMap.set(String(suggestionId), status);
      }
    });

    devLog("Successfully fetched suggestion statuses", {
      structureId,
      count: statusMap.size,
    });

    return statusMap;
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    suggestionsLog("❌ Error fetching suggestion statuses", {
      structureId,
      error: errorMsg,
    });
    return new Map();
  }
}

/**
 * Merge base suggestions with external statuses from suggestions_status collection
 * External status takes precedence: external status > base suggestion status > ""
 * 
 * Used to combine read-only suggestions array with writable status map in a single, efficient operation.
 * 
 * @param baseSuggestions - Array of base suggestion items (from suggestions/{id}.suggestions)
 * @param statusMap - Map of suggestion IDs to their external statuses (from suggestions_status)
 * @returns Merged suggestions array with statuses updated from statusMap
 */
export function mergeSuggestionsWithStatuses(
  baseSuggestions: unknown[],
  statusMap: Map<string, string>
): unknown[] {
  if (!Array.isArray(baseSuggestions) || statusMap.size === 0) {
    return baseSuggestions;
  }

  return baseSuggestions.map((category: unknown) => {
    if (!category || typeof category !== "object") {
      return category;
    }

    const cat = category as Record<string, unknown>;
    if (!Array.isArray(cat.items)) {
      return category;
    }

    // Merge items in this category with external statuses
    const mergedItems = (cat.items as unknown[]).map((item: unknown) => {
      if (!item || typeof item !== "object") {
        return item;
      }

      const itemObj = item as Record<string, unknown>;
      const itemId = String(itemObj.id ?? "");

      // Look up external status; if found, use it; otherwise keep original
      const externalStatus = itemId ? statusMap.get(itemId) : undefined;
      if (externalStatus !== undefined) {
        return { ...itemObj, status: externalStatus };
      }

      return item;
    });

    return { ...cat, items: mergedItems };
  });
}

/**
 * Bulk upsert suggestion statuses in suggestions_status collection
 * Efficiently writes multiple status updates using batch writes (max 500 per batch)
 * 
 * @param structureId - Structure unique identifier
 * @param updates - Array of { id: suggestionId, status: newStatus }
 * @param authUid - Current authenticated user ID
 * @param authEmail - Current authenticated user email (optional)
 * @returns Promise with success/error info
 */
export async function bulkUpsertSuggestionStatuses(
  structureId: string,
  updates: Array<{ id: string; status: string }>,
  authUid: string,
  authEmail?: string
): Promise<{ success: boolean; error?: string; count?: number }> {
  try {
    suggestionsLog("bulkUpsertSuggestionStatuses() called", {
      structureId,
      itemCount: updates.length,
      authUid,
    });

    if (!structureId || !updates.length || !authUid) {
      suggestionsLog("❌ Missing required params", {
        structureId: !!structureId,
        updateCount: updates.length,
        authUid: !!authUid,
      });
      return { success: false, error: "Missing structureId, updates, or authUid" };
    }

    logWritePath("suggestions_status", `${structureId}__*`, [], "batch-write");

    const batch = writeBatch(db);

    let writeCount = 0;
    updates.forEach(({ id: suggestionId, status }) => {
      const docId = `${structureId}__${safeId(suggestionId)}`;
      const ref = doc(db, "suggestions_status", docId);

      batch.set(
        ref,
        {
          structureId,
          suggestionId,
          status,
          updatedAt: serverTimestamp(),
          updatedBy: authUid,
          updatedByEmail: authEmail ?? null,
        },
        { merge: true }
      );

      writeCount++;
    });

    if (writeCount === 0) {
      return { success: true, error: "No updates to write" };
    }

    await batch.commit();

    devLog("Successfully bulk upserted suggestion statuses", {
      structureId,
      count: writeCount,
    });

    return { success: true, count: writeCount };
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    suggestionsLog("❌ Error bulk upserting suggestion statuses", {
      structureId,
      error: errorMsg,
    });
    return { success: false, error: errorMsg };
  }
}

/**
 * Debug helper: log write path with full context
 * Includes soft guard against writing to wrong collection
 * Visible in both dev and prod
 */
const logWritePath = (
  collectionName: string,
  docId: string,
  payloadKeys: string[],
  operation: string = "write"
) => {
  if (!shouldLogSuggestionsRepo()) {
    return;
  }

  const fullPath = `${collectionName}/${docId}`;
  const { uid, email } = getDebugContext();
  
  console.debug(
    `[SuggestionsRepo][DEBUG] ${operation} targetPath=${fullPath} authUid=${uid || "(none)"} authEmail=${email || "(none)"} payloadKeys=${JSON.stringify(payloadKeys)}`
  );

  // Soft guard: warn if writing to structures instead of suggestions (both dev and prod)
  if (collectionName === "structures" || fullPath.includes("/structures/")) {
    const msg = `[SuggestionsRepo][BUG] Writing to structures collection (${fullPath}), expected suggestions`;
    console.error("%c" + msg, "color: red; font-weight: bold;");
  }
};

/**
 * Normalize priority value from various formats
 */
const normalizePriority = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const raw = value.trim().toLowerCase();
    if (raw === "alta" || raw === "high") return "high";
    if (raw === "media" || raw === "medium") return "medium";
    if (raw === "bassa" || raw === "low") return "low";
    return value;
  }
  if (typeof value === "number") {
    if (value >= 3) return "high";
    if (value === 2) return "medium";
    if (value === 1) return "low";
  }
  return undefined;
};

/**
 * Normalize a single suggestion entry
 */
export function normalizeSuggestion(raw: unknown): Suggestion | null {
  if (!raw || typeof raw !== "object") return null;

  const entry = raw as Record<string, unknown>;
  
  // Extract ID
  const rawId = entry.id ?? entry._id;
  const id = typeof rawId === "string" || typeof rawId === "number" ? rawId : `suggestion-${Date.now()}-${Math.random()}`;

  // Extract interventi array
  const interventi = Array.isArray(entry.interventi)
    ? entry.interventi.map((item) => String(item)).filter(Boolean)
    : undefined;

  // Extract description with fallbacks
  const description =
    typeof entry.description === "string"
      ? entry.description
      : typeof entry.title === "string"
        ? entry.title
        : typeof entry.name === "string"
          ? entry.name
          : interventi && interventi.length > 0
            ? interventi.join(" ")
            : undefined;

  // Extract title with fallbacks
  const title = 
    typeof entry.title === "string" 
      ? entry.title 
      : typeof entry.name === "string"
        ? entry.name
        : description;

  // Normalize priority
  const priority = normalizePriority(
    entry.priority ?? entry.priorita ?? entry.priority_level
  );

  // Build normalized suggestion
  const normalized: Suggestion = {
    id,
    title,
    description,
    category: typeof entry.category === "string" ? entry.category : undefined,
    kind: typeof entry.kind === "string" ? entry.kind : undefined,
    expected: typeof entry.expected === "string" ? entry.expected : undefined,
    status: typeof entry.status === "string" ? entry.status : undefined,
    priority,
    module: typeof entry.module === "string" ? entry.module : undefined,
    moduleName: typeof entry.moduleName === "string" ? entry.moduleName : undefined,
    area: typeof entry.area === "string" ? entry.area : undefined,
    interventi,
  };

  // Preserve additional fields from original entry
  Object.keys(entry).forEach((key) => {
    if (!(key in normalized) && entry[key] !== undefined) {
      normalized[key] = entry[key];
    }
  });

  return normalized;
}

/**
 * Normalize an array of suggestions
 */
export function normalizeSuggestions(input: unknown): Suggestion[] {
  if (!Array.isArray(input)) return [];
  return input
    .map((item) => normalizeSuggestion(item))
    .filter((item): item is Suggestion => item !== null);
}

/**
 * Read suggestions from Firestore collection for a single structure
 */
async function readFromCollection(structureId: string): Promise<Suggestion[] | null> {
  try {
    const ref = getSuggestionDocRef(structureId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      devLog("Collection doc not found", { structureId });
      return null;
    }

    const data = snap.data() as Record<string, unknown> | undefined;
    const suggestionsRaw = data?.suggestions;

    if (!Array.isArray(suggestionsRaw) || suggestionsRaw.length === 0) {
      devLog("Collection doc exists but suggestions array is empty", { structureId });
      return null;
    }

    return normalizeSuggestions(suggestionsRaw);
  } catch (err) {
    devLog("Error reading from collection", { structureId, error: String(err) });
    return null;
  }
}

/**
 * Get suggestions for a single structure
 * 
 * @param structureUid - Structure unique identifier
 * @param legacy - Optional legacy suggestions array to use as fallback
 * @returns Array of normalized suggestions
 */
export async function getSuggestionsForStructure(
  structureId: string,
  legacy?: unknown[]
): Promise<Suggestion[]> {
  logModeOnce();
  suggestionsLog("getSuggestionsForStructure() called", { structureId, hasLegacy: !!legacy });
  
  if (!structureId) {
    suggestionsLog("❌ Empty structureId provided");
    return [];
  }

  const mode = getSuggestionsMode();
  
  // V2 mode: collection-first, fallback to legacy
  if (mode === "v2") {
    const fromCollection = await readFromCollection(structureId);
    
    if (fromCollection && fromCollection.length > 0) {
      devLog("Using suggestions from collection", {
        structureId,
        count: fromCollection.length,
      });
      return fromCollection;
    }
    
    // Fallback to legacy if collection is empty/missing
    if (Array.isArray(legacy) && legacy.length > 0) {
      devLog("V2 mode: collection empty, using legacy fallback", {
        structureId,
        count: legacy.length,
      });
      return normalizeSuggestions(legacy);
    }
    
    devLog("V2 mode: no suggestions found", { structureId });
    return [];
  }

  // Legacy mode: use only legacy data
  if (Array.isArray(legacy) && legacy.length > 0) {
    devLog("Legacy mode: using legacy suggestions", {
      structureId,
      count: legacy.length,
    });
    return normalizeSuggestions(legacy);
  }
  
  devLog("Legacy mode: no suggestions found", { structureId });
  return [];
}

/**
 * Get suggestions for multiple structures in batch
 * 
 * @param structureUids - Array of structure unique identifiers
 * @param legacyMap - Optional map of structure UID to legacy suggestions arrays
 * @returns Record mapping structure UID to array of normalized suggestions
 */
export async function getSuggestionsForStructures(
  structureUids: string[],
  legacyMap?: Record<string, unknown[]>
): Promise<Record<string, Suggestion[]>> {
  logModeOnce();
  suggestionsLog("getSuggestionsForStructures() called", { count: structureUids.length, hasLegacyMap: !!legacyMap });
  
  if (!Array.isArray(structureUids) || structureUids.length === 0) {
    suggestionsLog("❌ Empty structureUids array provided");
    return {};
  }

  const result: Record<string, Suggestion[]> = {};

  // Filter out empty/invalid UIDs
  const validUids = structureUids.filter((uid) => uid && String(uid).trim());

  if (validUids.length === 0) {
    devLog("No valid structureUids after filtering");
    return {};
  }

  const mode = getSuggestionsMode();

  // V2 mode: collection-first, fallback to legacy
  if (mode === "v2") {
    // Batch read from Firestore (max 10 per query due to 'in' operator limit)
    const batchSize = 10;
    const batches: string[][] = [];

    for (let i = 0; i < validUids.length; i += batchSize) {
      batches.push(validUids.slice(i, i + batchSize));
    }

    devLog("Batch reading suggestions", {
      totalStructures: validUids.length,
      batches: batches.length,
    });

    for (const batch of batches) {
      try {
        const q = query(
          collection(db, "suggestions"),
          where(documentId(), "in", batch)
        );

        const querySnapshot = await getDocs(q);

        querySnapshot.forEach((docSnap) => {
          const structureUid = docSnap.id;
          const data = docSnap.data() as Record<string, unknown> | undefined;
          const suggestionsRaw = data?.suggestions;

          if (Array.isArray(suggestionsRaw) && suggestionsRaw.length > 0) {
            result[structureUid] = normalizeSuggestions(suggestionsRaw);
            devLog("Loaded suggestions from collection", {
              structureUid,
              count: result[structureUid].length,
            });
          }
        });
      } catch (err) {
        devLog("Error batch reading from collection", {
          batch,
          error: String(err),
        });
      }
    }

    // For structures not found in collection, use legacy fallback
    for (const structureUid of validUids) {
      if (!result[structureUid] || result[structureUid].length === 0) {
        const legacyData = legacyMap?.[structureUid];
        if (Array.isArray(legacyData) && legacyData.length > 0) {
          result[structureUid] = normalizeSuggestions(legacyData);
          devLog("V2 mode: collection empty, using legacy fallback", {
            structureUid,
            count: result[structureUid].length,
          });
        } else {
          result[structureUid] = [];
          devLog("V2 mode: no suggestions found", { structureUid });
        }
      }
    }

    return result;
  }

  // Legacy mode: use only legacy data
  for (const structureUid of validUids) {
    const legacyData = legacyMap?.[structureUid];
    if (Array.isArray(legacyData) && legacyData.length > 0) {
      result[structureUid] = normalizeSuggestions(legacyData);
      devLog("Legacy mode: using legacy suggestions", {
        structureUid,
        count: result[structureUid].length,
      });
    } else {
      result[structureUid] = [];
      devLog("Legacy mode: no suggestions found", { structureUid });
    }
  }

  return result;
}

/**
 * Get suggestions result with source tracking for a single structure
 * (Compatibility with existing SuggestionsResult type)
 */
export async function getSuggestionsWithSource(
  structureUid: string,
  legacy?: unknown[]
): Promise<SuggestionsResult> {
  logModeOnce();
  suggestionsLog("getSuggestionsWithSource() called", { structureUid, hasLegacy: !!legacy });
  
  if (!structureUid) {
    suggestionsLog("❌ Empty structureUid provided");
    return { suggestions: [], source: "empty" };
  }

  const mode = getSuggestionsMode();

  // V2 mode: collection-first, fallback to legacy
  if (mode === "v2") {
    const fromCollection = await readFromCollection(structureUid);

    if (fromCollection && fromCollection.length > 0) {
      return { suggestions: fromCollection, source: "collection" };
    }

    // Fallback to legacy if collection is empty/missing
    if (Array.isArray(legacy) && legacy.length > 0) {
      devLog("V2 mode: collection empty, using legacy fallback", { structureUid });
      return { suggestions: normalizeSuggestions(legacy), source: "legacy" };
    }

    return { suggestions: [], source: "empty" };
  }

  // Legacy mode: use only legacy data
  if (Array.isArray(legacy) && legacy.length > 0) {
    devLog("Legacy mode: using legacy suggestions", { structureUid });
    return { suggestions: normalizeSuggestions(legacy), source: "legacy" };
  }
  
  return { suggestions: [], source: "empty" };
}

/**
 * DEPRECATED: Update suggestion item statuses
 * 
 * ⚠️ This function is deprecated and now delegates to the NEW suggestions_status collection.
 * It no longer writes to the old suggestions collection.
 * 
 * NOTE: Old behavior (writing to suggestions/{structureId}) is permanently disabled.
 * All writes now go to suggestions_status collection only.
 * 
 * @param structureUid - Structure unique identifier
 * @param statusByItemId - Map of item IDs to their new status values
 * @returns Promise that resolves when update completes
 * 
 * @see upsertSuggestionStatus - For single-item updates
 * @see bulkUpsertSuggestionStatuses - For batch updates
 * @deprecated Use {@link bulkUpsertSuggestionStatuses} instead with new suggestions_status collection
 */
export async function updateSuggestionStatuses(
  structureUid: string,
  statusByItemId: Record<string, string>
): Promise<{ success: boolean; error?: string }> {
  try {
    suggestionsLog("updateSuggestionStatuses() DEPRECATED wrapper called", { 
      structureUid, 
      itemCount: Object.keys(statusByItemId).length 
    });

    // Log deprecation warning
    const msg = "[SuggestionsRepo][DEPRECATED] updateSuggestionStatuses() is deprecated. Use bulkUpsertSuggestionStatuses() instead with new suggestions_status collection.";
    console.warn("%c" + msg, "color: orange; font-weight: bold;");

    if (!structureUid) {
      suggestionsLog("❌ Empty structureUid provided");
      return { success: false, error: "Empty structure ID" };
    }

    if (!statusByItemId || Object.keys(statusByItemId).length === 0) {
      suggestionsLog("❌ No status updates provided");
      return { success: false, error: "No status updates provided" };
    }

    // Delegate to new bulkUpsertSuggestionStatuses via suggestions_status collection
    const authUid = auth.currentUser?.uid;
    const authEmail = auth.currentUser?.email;

    if (!authUid) {
      suggestionsLog("❌ No authenticated user");
      return { success: false, error: "User not authenticated" };
    }

    // Convert map to update array
    const updates = Object.entries(statusByItemId).map(([id, status]) => ({
      id,
      status,
    }));

    const result = await bulkUpsertSuggestionStatuses(
      structureUid,
      updates,
      authUid,
      authEmail
    );

    return { success: result.success, error: result.error };
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    suggestionsLog("❌ Error in deprecated updateSuggestionStatuses", { 
      structureUid, 
      error: errorMsg 
    });
    return { success: false, error: errorMsg };
  }
}

/**
 * Bulk update suggestion item status (single item convenience wrapper)
 * 
 * @param structureUid - Structure unique identifier
 * @param itemId - Suggestion item ID
 * @param status - New status value
 */
export async function updateSuggestionStatus(
  structureUid: string,
  itemId: string,
  status: string
): Promise<{ success: boolean; error?: string }> {
  suggestionsLog("updateSuggestionStatus() wrapper called", { structureUid, itemId, status });
  return updateSuggestionStatuses(structureUid, { [itemId]: status });
}

